import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { LayoutTemplate, Trash2, Globe, Lock, User, Share2, Check } from "lucide-react";

export default function Templates() {
  const [mine, setMine] = useState([]);
  const [publicT, setPublic] = useState([]);
  const nav = useNavigate();

  const load = async () => {
    const d = await api.get("/workforce/templates").then((r) => r.data);
    setMine(d.mine || []); setPublic(d.public || []);
  };
  useEffect(() => { load(); }, []);

  const use = async (t) => {
    const r = await api.post(`/workforce/templates/${t.template_id}/use`).then((x) => x.data);
    sessionStorage.setItem("wf_prefill", JSON.stringify(r.prefill));
    nav("/workforce/new");
  };
  const del = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    await api.delete(`/workforce/templates/${t.template_id}`);
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="templates-page">
      <div className="flex items-center gap-2 mb-1">
        <LayoutTemplate size={20} strokeWidth={2.5} />
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Templates</div>
      </div>
      <h1 className="font-heading text-3xl md:text-4xl font-bold mb-6">Reusable AI workers</h1>

      <Section title="Your templates" empty="You haven't saved any templates yet. Open a worker → Settings → Save as template." icon={User}>
        {mine.map((t) => <TemplateCard key={t.template_id} t={t} onUse={use} onDelete={del} owned />)}
      </Section>

      <Section title="Community templates" empty="No public templates yet. Publish yours to share!" icon={Globe} className="mt-8">
        {publicT.map((t) => <TemplateCard key={t.template_id} t={t} onUse={use} />)}
      </Section>
    </div>
  );
}

function Section({ title, empty, icon: Icon, children, className = "" }) {
  const arr = React.Children.toArray(children);
  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} strokeWidth={2.5} className="text-slate-600" />
        <h2 className="font-heading text-xl font-bold">{title}</h2>
      </div>
      {arr.length === 0
        ? <div className="brut-card p-6 text-center text-sm text-slate-500 italic">{empty}</div>
        : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{arr}</div>}
    </div>
  );
}

function TemplateCard({ t, onUse, onDelete, owned }) {
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const share = async () => {
    setSharing(true);
    try {
      const r = await api.post(`/workforce/templates/${t.template_id}/share`).then((x) => x.data);
      const url = `${window.location.origin}${r.url_path}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } finally { setSharing(false); }
  };
  return (
    <div className="brut-card p-4 flex flex-col gap-2" data-testid={`template-${t.template_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl border-2 border-slate-900 bg-amber-100 flex items-center justify-center text-2xl shrink-0">
          {t.avatar || "🤖"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold truncate">{t.name}</div>
          <div className="text-xs text-slate-500 truncate">{t.title}</div>
        </div>
        {t.public
          ? <span className="text-[10px] uppercase font-bold text-emerald-700 flex items-center gap-0.5"><Globe size={10} /> Public</span>
          : <span className="text-[10px] uppercase font-bold text-slate-500 flex items-center gap-0.5"><Lock size={10} /> Private</span>}
      </div>
      {t.description && <p className="text-xs text-slate-600 line-clamp-2">{t.description}</p>}
      <div className="flex items-center justify-between text-xs mt-1">
        <span className="text-slate-500">by <b>{t.author_name}</b> · {t.uses || 0} uses</span>
        <div className="flex gap-2 items-center">
          {owned && (
            <>
              <button onClick={share} disabled={sharing} title="Share (makes public + copies link)"
                      className="text-slate-500 hover:text-slate-900" data-testid={`share-${t.template_id}`}>
                {copied ? <Check size={14} className="text-emerald-600" /> : <Share2 size={14} />}
              </button>
              <button onClick={() => onDelete(t)} className="text-slate-400 hover:text-red-500" data-testid={`del-${t.template_id}`}>
                <Trash2 size={14} />
              </button>
            </>
          )}
          <button onClick={() => onUse(t)} className="brut-btn brut-btn-outline text-xs py-1 px-2.5" data-testid={`use-${t.template_id}`}>
            Use →
          </button>
        </div>
      </div>
      {copied && <div className="text-[10px] text-emerald-600 font-bold" data-testid={`copied-${t.template_id}`}>✓ Link copied to clipboard</div>}
    </div>
  );
}
